/**
 * Build product href preferring CAS canonical URL when available.
 */
export function getProductHref(item) {
  if (!item) {
    return '/Product';
  }
  const cas = extractCas(item);
  if (cas) {
    return '/cas/' + encodeURIComponent(cas);
  }
  if (item.id != null) {
    return '/ProductDetail/' + item.id;
  }
  return '/Product';
}

export function extractCas(item) {
  if (!item) {
    return null;
  }
  if (item.cas && String(item.cas).trim()) {
    return String(item.cas).trim();
  }
  const attrs = item.attrs || [];
  for (let i = 0; i < attrs.length; i++) {
    const name = (attrs[i].name || '').toLowerCase();
    if (name === 'cas no.' || name === 'cas no' || name === 'cas') {
      const value = attrs[i].value;
      if (value && String(value).trim()) {
        return String(value).trim();
      }
    }
  }
  return null;
}

export function extractFormula(item) {
  if (!item) {
    return '';
  }
  if (item.formula) {
    return item.formula;
  }
  const attrs = item.attrs || [];
  for (let i = 0; i < attrs.length; i++) {
    if ((attrs[i].name || '') === 'Molecular formula') {
      return attrs[i].value || '';
    }
  }
  return '';
}

const SITE_ORIGIN = 'https://www.jhechem.com';
const BRAND = 'J&H Chemical';

function truncate(text, max) {
  if (!text || text.length <= max) {
    return text || '';
  }
  return text.slice(0, max - 1).trim() + '…';
}

export function buildProductTitle(name, cas) {
  const safeName = truncate(name || 'Chemical Product', 55);
  if (cas) {
    return `${safeName} | CAS ${cas} | ${BRAND}`;
  }
  return `${safeName} | ${BRAND}`;
}

export function buildProductDescription(name, cas, formula) {
  const safeName = (name || 'chemical product').trim();
  let desc = `Buy ${safeName}`;
  if (cas) {
    desc += ` (CAS ${cas})`;
  }
  desc += ' from Hangzhou J&H Chemical.';
  if (formula) {
    desc += ` Formula ${formula}.`;
  }
  desc += ' Request a quotation or custom synthesis.';
  return desc;
}

/**
 * Homepage document title, canonical, and Organization JSON-LD.
 */
export function applyHomeSeo() {
  const title = 'Hangzhou J&H Chemical Co., Ltd. | CAS Product Catalog';
  const description =
    'Search chemical products by CAS Registry Number. Custom synthesis, stock products, and quotations from Hangzhou J&H Chemical Co., Ltd.';
  const canonical = SITE_ORIGIN + '/';

  document.title = title;
  setMeta('description', description);
  setMeta('og:title', title, 'property');
  setMeta('og:description', description, 'property');
  setMeta('og:url', canonical, 'property');
  setLinkCanonical(canonical);

  setJsonLd('home-jsonld', {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: 'Hangzhou J&H Chemical Co., Ltd.',
        url: SITE_ORIGIN + '/',
        brand: { '@type': 'Brand', name: BRAND },
      },
      {
        '@type': 'WebSite',
        name: BRAND,
        url: SITE_ORIGIN + '/',
        potentialAction: {
          '@type': 'SearchAction',
          target: SITE_ORIGIN + '/Product?title={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  });
}

/**
 * Apply document title, description, canonical, and optional JSON-LD.
 */
export function applyProductSeo({ name, cas, formula, path }) {
  const safeName = name || 'Chemical Product';
  const title = buildProductTitle(safeName, cas);
  const description = buildProductDescription(safeName, cas, formula);

  document.title = title;
  setMeta('description', description);
  setMeta('og:title', title, 'property');
  setMeta('og:description', description, 'property');
  if (path) {
    const canonical = SITE_ORIGIN + path;
    setLinkCanonical(canonical);
    setMeta('og:url', canonical, 'property');
  }

  if (cas || name) {
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: safeName,
      brand: { '@type': 'Brand', name: BRAND },
      description,
    };
    if (cas) {
      jsonLd.mpn = cas;
      jsonLd.additionalProperty = [
        { '@type': 'PropertyValue', name: 'CAS Registry Number', value: cas },
      ];
      if (formula) {
        jsonLd.additionalProperty.push({
          '@type': 'PropertyValue',
          name: 'Molecular Formula',
          value: formula,
        });
      }
    }
    setJsonLd('product-jsonld', jsonLd);
  }
}

function setMeta(name, content, attr) {
  const key = attr || 'name';
  let el = document.head.querySelector(`meta[${key}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(key, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLinkCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
